/**
 * aera-lp-track.js — o "diário de bordo" de cada visita nas páginas do layout aera-lp.
 *
 * PARA QUE EXISTE
 * Registrar no MenoCore a jornada de cada visita das páginas da Shopify — entrada (init),
 * tempo na página, rolagem, cliques de compra e de WhatsApp, fechamento — na coleção
 * `lp_sessions`, a mesma das páginas da Vercel. Até 25/09/2026 só as páginas da Vercel
 * tinham isso (decisão do teste de stack de 24/08/2026, ETAPA2_PORTE.md §6.3); desde 22/09
 * a Shopify é a loja de verdade, e sem este arquivo o MenoCore via acessos só pelo GA4
 * (número agregado) e vendas só pela Yampi, sem ligar a venda à visita. É a pendência P25 do
 * dossiê docs/implementations/2026-09-23-lp-ads-kit-alta-performance/ (MenoCore), decisão
 * D31 da tabela DECISOES.md desse dossiê, plano em historico/2026-09-25-0755-BRT.md.
 *
 * DE ONDE FOI PORTADO
 * hooks/useSessionTracking.js do MenoCore (React), sem o React e SEM os pushes de GTM —
 * esses continuam no aera-lp.js, que espelha os eventos dele para cá quando
 * `window.aeraLpTrack` existe. É arquivo próprio, e não parte do aera-lp.js, para ser
 * testável sozinho no Jest do MenoCore e para não engordar um arquivo que já passa de 2.200
 * linhas. Sem este arquivo o aera-lp.js segue funcionando: cada chamada dele é guardada por
 * `if (window.aeraLpTrack)`.
 *
 * O CONTRATO DO trackEvent — onde mais se erra
 *   trackEvent('event', { eventType: 'faq_toggle', meta: { question: 'x' } })   // certo
 *   trackEvent('faq_toggle', { question: 'x' })                                  // ERRADO
 * O primeiro argumento é sempre a action do protocolo (init | event | sync | close); o nome
 * do evento vai em `eventType`. A forma errada recebe HTTP 400 e falha em silêncio — o
 * evento simplesmente some, e a perda só aparece num número menor semanas depois. Ver
 * skills/lp-tracking/SKILL.md no MenoCore.
 * Desde 29/09/2026 todo `event` leva também `page_meta` (o mesmo do sync, ver pageMeta) e
 * `browser_features`, postos aqui dentro — quem chama não passa nada disso. Se o `init` se
 * perdeu, o primeiro `event` basta para o servidor criar a sessão completa, em vez de um doc
 * órfão sem página, UTMs nem fbclid (D4 do dossiê
 * docs/implementations/2026-09-29-analytics-trafego-desempenho/ no MenoCore). `init` e `event`
 * saem com keepalive (ver trackEvent).
 *
 * AMBIENTE DE TESTE É DECIDIDO NO SERVIDOR — E O RASCUNHO SE DECLARA
 * A rota /api/lp-track grava `traffic_type: TEST` em dois casos: (a) a origem da página
 * (cabeçalho `Origin`) está fora de *.aera40mais.com.br — é o caso do `theme dev`
 * (127.0.0.1:9292); (b) a página se declara de rascunho pelo `theme_role` que este arquivo
 * manda (ver themeRole). O rascunho PRECISA se declarar porque a origem não o distingue:
 * medido na revisão de 25/09/2026, a Shopify responde 301 do host myshopify para o domínio
 * primário, e a página com `?preview_theme_id=...` é servida em https://aera40mais.com.br, a
 * MESMA origem da produção. O `theme_role` vem de `theme.role` do Liquid ('main' no tema
 * publicado, 'unpublished' ou 'development' nos outros) e vira 'design_mode' dentro do editor
 * do tema. O sinal só REBAIXA a sessão para TEST, nunca promove — o mesmo modelo de confiança
 * das UTMs de QA (isQaPayload na rota). Por isso este arquivo NÃO olha o setting
 * `marketing_tags_enabled` do tema nem o portão shouldBlockMarketing do aera-lp.js: ele
 * sempre roda, e o servidor separa. É analytics próprio, não tag de marketing.
 *
 * TESTE
 * __tests__/unit/theme/aeraLpTrack.test.js no MenoCore carrega este arquivo num jsdom e
 * confere, entre outros, que a lista de crawlers abaixo é idêntica à de lib/botDetection.js.
 */
(function () {
  'use strict';

  /* ==========================================================================
     0. Constantes
     ========================================================================== */
  var ENDPOINT = 'https://loja.aera40mais.com.br/api/lp-track';
  var SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutos sem atividade = sessão nova (igual ao hook)
  var QA_UTM_SOURCE = 'menocore_qa';
  var QA_UTM_MEDIUM = 'test';

  /* Cópia LITERAL de lib/botDetection.js → BOT_UA_PATTERNS (MenoCore). A fonte é aquele
     arquivo; esta lista não é editada aqui. O teste de paridade em
     __tests__/unit/theme/aeraLpTrack.test.js QUEBRA se as duas divergirem — é a rede que
     impede o servidor e o navegador de discordarem sobre quem é crawler.

     Por que a lista mora no navegador: o crawler honesto se declara no user-agent, e barrá-lo
     aqui custa zero de servidor. Em 21/09/2026 o `meta-externalads` (revisão de anúncios da
     Meta) executou o JS das páginas da Vercel, abriu sessão, bateu heartbeat/sync/close e
     estourou a cota mensal do Hobby num dia (675K requisições). Bot que mente o UA continua
     sendo julgado no servidor por `detectBot`. */
  var BOT_UA_PATTERNS = [
    'bot', 'crawler', 'spider', 'headless', 'lighthouse', 'mediapartners',
    'adsbot', 'facebookexternalhit', 'googlebot', 'googleother', 'bingbot', 'yandexbot',
    'duckduckbot', 'baiduspider', 'twitterbot', 'linkedinbot', 'slackbot',
    'meta-externalads', 'whatsapp', 'chatgpt', 'gptbot', 'claudebot',
    'oai-searchbot', 'perplexity', 'applebot',
    'python-requests', 'axios', 'curl', 'wget', 'node-fetch',
    'phantom', 'puppeteer', 'playwright', 'selenium'
  ];

  var MARCOS_DE_ROLAGEM = [25, 50, 75, 100];
  var SINAIS_HUMANOS = ['mousemove', 'touchstart', 'keydown', 'scroll'];

  /* ==========================================================================
     1. Helpers puros
     ========================================================================== */
  function noop() {}

  function assign(alvo, origem) {
    if (origem) {
      Object.keys(origem).forEach(function (k) { alvo[k] = origem[k]; });
    }
    return alvo;
  }

  /* crypto.randomUUID só existe em contexto seguro (https, localhost). Fora dele, o mesmo
     fallback do hook: instante + aleatório em base36. */
  function uuid() {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch (err) { /* contexto inseguro: cai no fallback */ }
    return String(Date.now()) + '-' + Math.random().toString(36).slice(2);
  }

  /* localStorage pode lançar (modo privado do Safari antigo, cota, storage bloqueado). Toda
     leitura e escrita passa por aqui para a falha nunca chegar à página. */
  function lerStorage(chave) {
    try { return localStorage.getItem(chave); } catch (err) { return null; }
  }

  function lerInteiro(chave) {
    var valor = parseInt(lerStorage(chave) || '0', 10);
    return isNaN(valor) ? 0 : valor;
  }

  function gravarStorage(chave, valor) {
    try { localStorage.setItem(chave, valor); } catch (err) { /* sem storage: só a memória */ }
  }

  function isBotUserAgent(userAgent) {
    var ua = (userAgent || '').toLowerCase();
    for (var i = 0; i < BOT_UA_PATTERNS.length; i++) {
      if (ua.indexOf(BOT_UA_PATTERNS[i]) !== -1) return true;
    }
    return false;
  }

  /* Crawler declarado no user-agent: nada roda — sem init, sem timers, sem listeners, sem
     decoração de link. Mesma regra do isCrawlerClient do hook (21/09/2026). */
  function isCrawlerClient() {
    if (typeof navigator === 'undefined') return false;
    return isBotUserAgent(navigator.userAgent);
  }

  /* O par de UTMs de QA. Quem grava `traffic_type: TEST` por causa dele é o servidor
     (isQaPayload lê utm_source/utm_medium do corpo); aqui só existe para o aera-lp.js poder
     perguntar sem manter uma segunda cópia das constantes. */
  function isQaMode(search) {
    var params = new URLSearchParams(search === undefined ? window.location.search : search);
    return params.get('utm_source') === QA_UTM_SOURCE && params.get('utm_medium') === QA_UTM_MEDIUM;
  }

  function getUTMs() {
    var params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      fbclid: params.get('fbclid') || ''
    };
  }

  function device() {
    return window.innerWidth <= 768 ? 'mobile' : 'desktop';
  }

  /* Qual tema renderizou esta página, para o servidor separar rascunho de produção
     (revisão de 25/09/2026). O layout/aera-lp.liquid publica `window.AERA_LP_THEME_ROLE`
     (string: `theme.role` — 'main' no tema publicado, 'unpublished' ou 'development' nos
     outros) e `window.AERA_LP_DESIGN_MODE` (boolean: `request.design_mode`, o editor do
     tema). No editor a resposta é 'design_mode', que também não é 'main' e por isso vira
     TEST no servidor. Devolve '' quando o layout não publicou nada (tema antigo): aí o campo
     não vai, e o servidor segue a regra da origem, como antes. Só o servidor decide; este
     valor é um sinal que pode rebaixar a própria sessão para TEST, nunca o contrário. */
  function themeRole() {
    if (window.AERA_LP_DESIGN_MODE === true) return 'design_mode';
    var role = window.AERA_LP_THEME_ROLE;
    return (typeof role === 'string') ? role : '';
  }

  function timezone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (err) { return ''; }
  }

  /* ==========================================================================
     2. Identidade — as MESMAS chaves de localStorage do hook
     ==========================================================================
     lp_user_id                     quem é a pessoa (dura entre visitas)
     lp_session_id                  a visita atual (30 min sem atividade, ou UTM nova = outra)
     lp_session_last_activity       último toque, para o TTL
     lp_session_utms                UTMs com que a sessão nasceu, para detectar aquisição nova
     lp_session_accumulated_seconds tempo já contado, para o F5 não zerar a duração
     lp_session_max_scroll          rolagem máxima, para o F5 não re-disparar os marcos

     Mesmas chaves de propósito: o domínio raiz é da Shopify e o localStorage é por origem,
     então uma pessoa que vier de uma página da Vercel não compartilha nada com esta — mas o
     painel e o schema de `lp_sessions` são um só, e nomes diferentes só criariam uma segunda
     linguagem para o mesmo dado.
     ========================================================================== */
  function getUserId() {
    try {
      var uid = localStorage.getItem('lp_user_id');
      if (!uid) {
        var legacySid = localStorage.getItem('lp_session_id');
        uid = legacySid || uuid();
        localStorage.setItem('lp_user_id', uid);
      }
      return uid;
    } catch (err) {
      if (!window.__lp_user_id) window.__lp_user_id = uuid();
      return window.__lp_user_id;
    }
  }

  function getSessionId() {
    try {
      var now = Date.now();
      var utms = getUTMs();
      var utmString = JSON.stringify(utms);

      var sid = localStorage.getItem('lp_session_id');
      var lastActivity = parseInt(localStorage.getItem('lp_session_last_activity') || '0', 10);
      var savedUtms = localStorage.getItem('lp_session_utms') || '';
      var params = new URLSearchParams(window.location.search);
      var urlSessionId = params.get('lp_session_id') || '';
      var carriesCurrentSession = !!sid && urlSessionId === sid;
      var hasAcquisitionUtms = Object.keys(utms).some(function (k) { return !!utms[k]; });
      var isNewAcquisition = hasAcquisitionUtms && utmString !== savedUtms && !carriesCurrentSession;

      // Um id na URL igual ao da sessão prova que a navegação veio da própria jornada, mesmo
      // que links legados tenham acrescentado UTMs internas. Id ESTRANGEIRO nunca é adotado
      // (regra 1 da skill lp-tracking: a sessão segue pelo localStorage, não pela URL);
      // aquisição nova e TTL continuam abrindo uma sessão local nova.
      var isExpired = !sid || (now - lastActivity > SESSION_TTL_MS) || isNewAcquisition;

      if (isExpired) {
        sid = uuid();
        localStorage.setItem('lp_session_id', sid);
        localStorage.setItem('lp_session_utms', utmString);
        localStorage.setItem('lp_session_accumulated_seconds', '0');
        // Acréscimo em relação ao hook (25/09/2026): o hook zera só os segundos aqui e deixa
        // o `lp_session_max_scroll` da sessão anterior no storage. Quando o resetador ainda
        // não existe (primeiro carregamento da página), a sessão nova nascia herdando a
        // rolagem da anterior: o `sync` mandava max_scroll velho e os marcos não disparavam.
        // Aqui a rolagem zera junto, e a restauração do carregamento vale só para F5 dentro
        // da mesma sessão, que é o caso que ela existe para cobrir (ETAPA2_PORTE.md §6.3).
        localStorage.setItem('lp_session_max_scroll', '0');
        if (window.__resetTrackingRefs) window.__resetTrackingRefs();
      }

      localStorage.setItem('lp_session_last_activity', String(now));
      return sid;
    } catch (err) {
      if (!window.__lp_session_id) window.__lp_session_id = uuid();
      return window.__lp_session_id;
    }
  }

  /* Lê os ids SEM a lógica de expiração — é o que o appendUTMs do hook faz para decorar o
     link de checkout. Passar por getSessionId aqui abriria uma sessão nova sem `init` só
     porque alguém montou uma URL. Devolve '' quando não há, e sempre '' para crawler: a
     regra 1 da skill lp-tracking diz que identidade só vai em destino externo, e crawler
     não recebe decoração nenhuma. */
  function storedIds() {
    var vazio = { lp_session_id: '', lp_user_id: '' };
    if (isCrawlerClient()) return vazio;
    try {
      return {
        lp_session_id: localStorage.getItem('lp_session_id') || '',
        lp_user_id: localStorage.getItem('lp_user_id') || ''
      };
    } catch (err) {
      // Sem localStorage a sessão vive em memória (getSessionId/getUserId acima).
      return {
        lp_session_id: window.__lp_session_id || '',
        lp_user_id: window.__lp_user_id || ''
      };
    }
  }

  /* ==========================================================================
     3. trackEvent — o envio
     ==========================================================================
     Por que `text/plain` e não `application/json`: a página está em aera40mais.com.br e a
     API em loja.aera40mais.com.br — origens diferentes. `application/json` obriga o navegador
     a fazer um preflight (OPTIONS) antes de cada envio, dobrando as chamadas na cota da
     Vercel; `text/plain` é tipo "simples" do CORS e vai direto. E o `sendBeacon` do
     fechamento não tem escolha: o Chrome RECUSA beacon `application/json` entre origens
     (só tipos simples passam). O servidor lê o corpo como JSON de qualquer jeito
     (`request.json()` ignora o Content-Type).

     Por que `credentials: 'include'`: a rota lê o cookie `_ga` do cabeçalho para gravar o
     client_id do GA4 na sessão (applyGaIds em app/api/lp-track/route.js). Sem isto o
     navegador não manda cookie para outra origem e a costura com o GA4 some. As duas origens
     são o mesmo site (aera40mais.com.br), então o cookie Lax do GA viaja normalmente.
     ========================================================================== */
  var lastEvents = {};   // chave do evento → instante do último envio (dedupe de 100 ms)
  var initPromise = null;

  function enviar(body, keepalive) {
    return fetch(ENDPOINT, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      keepalive: !!keepalive,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: body
    });
  }

  function trackEvent(action, payload) {
    if (isCrawlerClient()) return Promise.resolve();
    payload = payload || {};
    var eventId = uuid();

    // Eventos depois do init esperam o init resolver: o servidor cria o documento no init, e
    // um evento que chegasse antes disputaria a criação com ele. Se o init se perder de vez,
    // o `page_meta` do evento (abaixo) é o que permite ao servidor criar o doc completo.
    var espera = (action !== 'init' && initPromise) ? initPromise : Promise.resolve();

    return espera.then(function () {
      // Dedupe: o mesmo evento com a mesma meta em menos de 100 ms é um só (clique duplo,
      // dois listeners para o mesmo gesto).
      var eventKey = action + '_' + (payload.eventType || '') + '_' + JSON.stringify(payload.meta || {});
      var now = Date.now();
      if (lastEvents[eventKey] && (now - lastEvents[eventKey] < 100)) return undefined;
      lastEvents[eventKey] = now;

      var sessionId = getSessionId();
      var lpUserId = getUserId();
      if (!sessionId) return undefined;

      var body = assign({}, getUTMs());
      body.action = action;
      body.sessionId = sessionId;
      body.lpUserId = lpUserId;
      // O proof de tempo (base36 do instante) obriga quem chama a executar JS de verdade; o
      // servidor rejeita proof com mais de 2 min ou no futuro.
      body.proof = Date.now().toString(36);
      body.eventId = eventId;
      assign(body, payload);

      // O sinal de rascunho vai no topo do corpo no `init` e no `event`; no `sync` e no
      // `close` ele já vai dentro de `page_meta` (ver pageMeta), que é o que o servidor lê
      // quando o doc precisa ser completado. Ausente = tema sem o Liquid novo, e o servidor
      // decide só pela origem.
      if (action === 'init' || action === 'event') {
        var role = themeRole();
        if (role) body.theme_role = role;
      }

      // `page_meta` e `browser_features` em todo `event` (29/09/2026), no mesmo formato do
      // `sync`: o servidor cria o doc completo pelo primeiro `event` quando o `init` não chegou
      // (52 docs órfãos desde 22/09, inclusive a sessão da compra da Soeli em 28/09), com o
      // mesmo detectBot do `init`. Só depois do start(): antes dele não há página montada.
      if (action === 'event' && mountedPage) {
        body.page_meta = pageMeta();
        body.browser_features = browserFeatures;
      }

      // `keepalive` no `init` e no `event`, não no `sync`. Sem ele o navegador CANCELA o fetch
      // em voo quando a página navega ou some:
      // - `event` (revisão de 25/09/2026): o clique de compra é o evento mais importante do
      //   funil e sai um instante ANTES da navegação ao checkout. O irAoCheckout do aera-lp.js
      //   faz um Promise.race de 300 ms em que o push resolve na hora — a página navega no
      //   microtask seguinte, e `checkout_button_clicked` se perdia justamente na sessão que
      //   converteu.
      // - `init` (29/09/2026): na sessão da Soeli (Instagram in-app, iPhone) chegaram os
      //   `event`, que já tinham keepalive, e não chegaram o `init` nem o `sync`, que não
      //   tinham. É o único padrão que separa o que chegou do que não chegou.
      // Os dois corpos têm menos de 2 KB, longe do limite de 64 KB em voo por origem que o
      // keepalive divide com o sendBeacon. O `sync` fica sem: é periódico, só sai com a aba
      // visível, e o `close` (sendBeacon) cobre o fechamento.
      return enviar(JSON.stringify(body), action === 'event' || action === 'init');
    }).then(noop, noop); // Falha silenciosa por contrato: analytics nunca quebra a página.
  }

  /* ==========================================================================
     4. Ciclo de vida da visita — porte do useEffect do hook
     ========================================================================== */
  var maxScroll = 0;
  var scrollMilestones = {};
  var lastActiveAt = Date.now();
  var accumulatedSeconds = 0;
  var heartbeatSent = false;
  var browserFeatures = null;
  var mountedSessionId = '';
  var mountedLpUserId = '';
  var mountedPage = '';

  /* Chamado por getSessionId quando abre sessão nova (TTL vencido ou UTM de aquisição nova
     no meio da visita). É o `window.__resetTrackingRefs` do hook, com o mesmo nome para o
     contrato entre as duas funções ficar reconhecível de um arquivo para o outro. */
  function resetTrackingRefs() {
    lastActiveAt = Date.now();
    accumulatedSeconds = 0;
    maxScroll = 0;
    scrollMilestones = {};
    heartbeatSent = false;
    gravarStorage('lp_session_accumulated_seconds', '0');
    gravarStorage('lp_session_max_scroll', '0');
  }

  function tempoTotal() {
    // Sempre soma o delta: se o visibilitychange já rodou, lastActiveAt foi zerado e o delta
    // é ~0, então não conta duas vezes.
    var delta = Math.round((Date.now() - lastActiveAt) / 1000);
    return accumulatedSeconds + delta;
  }

  /* page_meta de fallback do sync/close/event: se o init se perdeu (rede lenta, aba fechada
     cedo), o servidor cria o documento a partir disto. SEM pageVersion (decisão 4 do plano de
     25/09/2026): a variante da página da Shopify é resolvida no MenoCore por hostname +
     caminho + janela (lib/lpExternalArmMatcher.js), o mesmo caminho do gasto e do GA4. Assim
     a sessão ganha dono no painel sem a página declarar versão técnica, e uma página sem
     variante (ex. /pages/loja-kit) fica sem dono, nunca creditada a outra. */
  function pageMeta() {
    var meta = {
      page: mountedPage,
      device: device(),
      referrer: document.referrer || ''
    };
    assign(meta, getUTMs());
    // Sinal de rascunho no sync/close/event: o servidor lê `page_meta.theme_role` (ver themeRole).
    var role = themeRole();
    if (role) meta.theme_role = role;
    return meta;
  }

  function doSync() {
    if (document.visibilityState !== 'visible') return;
    trackEvent('sync', {
      sessionId: mountedSessionId,
      lpUserId: mountedLpUserId,
      duration_seconds: tempoTotal(),
      max_scroll: maxScroll,
      browser_features: browserFeatures,
      page_meta: pageMeta()
    });
  }

  function handleScroll() {
    var denominator = document.documentElement.scrollHeight - window.innerHeight;
    if (denominator <= 0) return; // viewport dinâmico ainda instável (iOS Safari)
    var scrollY = window.scrollY || window.pageYOffset || 0;
    var scrollPercent = Math.max(0, Math.min(100, Math.round((scrollY / denominator) * 100)));
    if (scrollPercent > maxScroll) maxScroll = scrollPercent;
    MARCOS_DE_ROLAGEM.forEach(function (m) {
      if (scrollPercent >= m && !scrollMilestones[m]) {
        scrollMilestones[m] = true;
        trackEvent('event', { eventType: 'scroll_' + m, meta: { max_scroll: scrollPercent } });
      }
    });
  }

  /* Fechamento por sendBeacon: é o único envio que o navegador garante entregar quando a aba
     some. Blob `text/plain` pelo motivo explicado na seção 3. Sem sendBeacon (navegador
     antigo), fetch com keepalive é o melhor esforço. */
  function beacon(body) {
    try {
      if (typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
        return;
      }
    } catch (err) { /* cai no fetch */ }
    try {
      enviar(body, true).then(noop, noop);
    } catch (err) { /* sem fetch: nada a fazer */ }
  }

  /* Um fechamento de aba dispara handleClose até TRÊS vezes no mesmo instante
     (visibilitychange→hidden, pagehide e beforeunload), e cada uma era um beacon `close` —
     três chamadas à rota, com o mesmo conteúdo, contra a cota da Vercel (P29 do dossiê). O
     intervalo mínimo de 1 s deixa passar o primeiro e engole os gêmeos; um close de verdade
     posterior (aba que voltou e escondeu de novo) passa normalmente. O storage é gravado
     SEMPRE, fora do filtro: é barato e garante que o F5 leia o valor mais recente. */
  var CLOSE_MIN_INTERVAL_MS = 1000;
  var lastCloseAt = 0;

  function handleClose() {
    var total = tempoTotal();
    gravarStorage('lp_session_accumulated_seconds', String(total));
    gravarStorage('lp_session_max_scroll', String(maxScroll));

    var now = Date.now();
    if (now - lastCloseAt < CLOSE_MIN_INTERVAL_MS) return;
    lastCloseAt = now;

    beacon(JSON.stringify({
      action: 'close',
      sessionId: mountedSessionId,
      lpUserId: mountedLpUserId,
      proof: now.toString(36),
      duration_seconds: total,
      max_scroll: maxScroll,
      browser_features: browserFeatures,
      page_meta: pageMeta()
    }));
  }

  function onVisibilityChange() {
    var now = Date.now();
    if (document.visibilityState === 'hidden') {
      accumulatedSeconds += Math.round((now - lastActiveAt) / 1000);
      lastActiveAt = now; // zera para o handleClose não somar de novo
      gravarStorage('lp_session_accumulated_seconds', String(accumulatedSeconds));
      gravarStorage('lp_session_max_scroll', String(maxScroll));
      handleClose(); // envia parcial ao esconder: no iOS é muitas vezes o único fechamento
    } else {
      lastActiveAt = now;
    }
  }

  function start() {
    // Crawler: nada roda. Sem init, sem timers, sem listeners. Custo zero, aqui e no servidor.
    if (isCrawlerClient()) return;

    // O resetador precisa existir ANTES da primeira getSessionId: se esta carga abrir sessão
    // nova, os contadores em memória e no storage zeram pelo mesmo caminho de sempre.
    window.__resetTrackingRefs = resetTrackingRefs;
    lastActiveAt = Date.now();

    // Identidade e URL desta visualização ficam fixas até o fechamento, como no hook.
    mountedSessionId = getSessionId();
    mountedLpUserId = getUserId();
    mountedPage = window.location.href;

    // Restaura tempo e rolagem da sessão atual (F5). Sem restaurar os marcos, cada F5
    // re-disparava scroll_25…100 e inflava o número (ETAPA2_PORTE.md §6.3).
    accumulatedSeconds = lerInteiro('lp_session_accumulated_seconds');
    maxScroll = lerInteiro('lp_session_max_scroll');
    MARCOS_DE_ROLAGEM.forEach(function (m) { if (maxScroll >= m) scrollMilestones[m] = true; });

    browserFeatures = {
      screen_res: (window.screen ? window.screen.width + 'x' + window.screen.height : ''),
      viewport: window.innerWidth + 'x' + window.innerHeight,
      timezone: timezone(),
      language: navigator.language || '',
      webdriver: navigator.webdriver || false
    };

    // Init: abre a sessão no servidor. Sem pageVersion (ver pageMeta).
    var payloadInit = {
      sessionId: mountedSessionId,
      lpUserId: mountedLpUserId,
      page: mountedPage,
      referrer: document.referrer || '',
      user_agent: navigator.userAgent,
      device: device(),
      browser_features: browserFeatures
    };
    initPromise = trackEvent('init', assign(payloadInit, getUTMs()));

    // Batimento aos 5 s: sinal de vida antes do primeiro sync, para sessões curtas no iOS.
    setTimeout(function () {
      if (document.visibilityState === 'visible' && !heartbeatSent) {
        trackEvent('event', { eventType: 'heartbeat_5s', meta: { duration_seconds: tempoTotal() } });
        heartbeatSent = true;
      }
    }, 5000);

    // Primeiro sync aos 10 s (pega sessão curta e cobre init perdido), depois a cada 30 s.
    setTimeout(doSync, 10000);
    setInterval(doSync, 30000);

    // human_verified: o primeiro gesto real, uma vez só. As mesmas opções no add e no
    // remove para o navegador casar o listener (removeEventListener compara o `capture`).
    var opcoesHumano = { once: true, passive: true };
    var verifyHuman = function () {
      trackEvent('event', { eventType: 'human_verified' });
      SINAIS_HUMANOS.forEach(function (tipo) { window.removeEventListener(tipo, verifyHuman, opcoesHumano); });
    };
    SINAIS_HUMANOS.forEach(function (tipo) { window.addEventListener(tipo, verifyHuman, opcoesHumano); });

    window.addEventListener('scroll', handleScroll, { passive: true });

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', handleClose);
    // beforeunload mantido como fallback para desktops antigos, como no hook.
    window.addEventListener('beforeunload', handleClose);
  }

  /* ==========================================================================
     5. Superfície pública — o que o aera-lp.js usa
     ========================================================================== */
  window.aeraLpTrack = {
    trackEvent: trackEvent,
    getSessionId: getSessionId,
    getUserId: getUserId,
    isCrawlerClient: isCrawlerClient,
    isQaMode: isQaMode,
    storedIds: storedIds,
    BOT_UA_PATTERNS: Object.freeze(BOT_UA_PATTERNS.slice())
  };

  // Os dois scripts do layout são `defer`, então aqui o HTML já foi lido: start() roda na
  // hora. O ramo do DOMContentLoaded cobre quem incluir o arquivo sem defer.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
